import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

interface UseTestProgressProps {
  isTestActive: boolean
  hasAnswers: boolean
  testTitle?: string
}

/**
 * テスト進行中の画面遷移を監視し、警告を表示するカスタムフック
 */
export const useTestProgress = ({
  isTestActive,
  hasAnswers,
  testTitle = 'テスト',
}: UseTestProgressProps) => {
  const location = useLocation()

  // ブラウザのページ離脱警告
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isTestActive && hasAnswers) {
        const message = `${testTitle}の回答が保存されていません。本当にページを離れますか？`
        event.preventDefault()
        event.returnValue = message // Chrome用
        return message // Safari用
      }
    }

    const handlePopState = (event: PopStateEvent) => {
      if (isTestActive && hasAnswers) {
        const confirmLeave = window.confirm(
          `${testTitle}実施中です。\n\n` +
            '画面を離れると、これまでの回答は保存されません。\n' +
            '本当に画面を離れますか？'
        )

        if (!confirmLeave) {
          // ブラウザの戻るボタンをキャンセル
          event.preventDefault()
          window.history.pushState(null, '', location.pathname)
        }
      }
    }

    if (isTestActive) {
      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('popstate', handlePopState)

      // 初回実行時にhistory stateを設定
      window.history.pushState(null, '', location.pathname)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isTestActive, hasAnswers, testTitle, location.pathname])

  // ナビゲーション前の確認関数
  const confirmNavigation = useCallback(
    (targetPath: string, targetName: string): boolean => {
      if (!isTestActive || !hasAnswers) {
        return true // テスト実施中でないか、回答がない場合は自由に遷移
      }

      return window.confirm(
        `${testTitle}実施中です。\n\n` +
          `「${targetName}」に移動すると、これまでの回答は保存されません。\n` +
          '本当に移動しますか？\n\n' +
          '※テストを継続する場合は「キャンセル」を選択してください。'
      )
    },
    [isTestActive, hasAnswers, testTitle]
  )

  return {
    confirmNavigation,
  }
}
